import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, BadgeDollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { instructorAPI, shopAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import {
  DataTable, EmptyPanel, FilterPills, PageHeader, PersonCell, SearchBox, StatCards, TableRow,
  fmtDate, money, statusBadge,
} from '../../components/adminUi';

// Admin 7 · Payouts (s134 redesign): stat cards (pending / paid out / owed),
// status pills with counts + search, requests table (instructor, amount,
// method, available balance, requested, Reject / Mark as paid) and a standing
// reminder that "Mark as paid" only records a payment made outside the app.
const STATUSES = ['pending', 'paid', 'rejected'];
const COLS = [
  { label: 'Instructor', width: 'minmax(0,2.2fr)' },
  { label: 'Amount', width: '120px' },
  { label: 'Method', width: 'minmax(0,1.4fr)' },
  { label: 'Available balance', width: '150px' },
  { label: 'Requested', width: '120px' },
  { label: 'Actions', width: '250px', align: 'right' },
];

export default function PayoutsPage() {
  const [tab, setTab] = useState('pending');
  const [all, setAll] = useState(null);
  const [search, setSearch] = useState('');
  const [instructorShare, setInstructorShare] = useState(null);

  const load = useCallback(() => {
    instructorAPI.adminPayouts('')
      .then(({ data }) => setAll(data.payouts || []))
      .catch(() => toast.error(t('Could not load payouts')));
  }, []);
  useEffect(() => {
    load();
    shopAPI.revenueReport().then(({ data }) => setInstructorShare(data.totals.instructor_revenue)).catch(() => {});
  }, [load]);

  const statusOf = (p) => p.status_label || 'pending';
  const list = all || [];
  const counts = useMemo(() => Object.fromEntries(STATUSES.map((s) => [s, list.filter((p) => statusOf(p) === s).length])), [list]);
  const sum = (s) => list.filter((p) => statusOf(p) === s).reduce((n, p) => n + Number(p.amount || 0), 0);
  const pendingSum = sum('pending');
  const paidSum = sum('paid');
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((p) => statusOf(p) === tab)
      .filter((p) => !q || `${(p.member || {}).name || ''} ${(p.member || {}).email || ''}`.toLowerCase().includes(q));
  }, [list, tab, search]);

  const resolve = async (p, action) => {
    const label = action === 'pay'
      ? `${t('Mark as PAID')}: ${money(p.amount)} → ${(p.member || {}).name}? ${t('(pay them first by the requested method)')}`
      : `${t('Reject')} ${money(p.amount)}?`;
    if (!window.confirm(label)) return;
    try {
      await instructorAPI.resolvePayout(p.id, action);
      toast.success(action === 'pay' ? t('Marked as paid') : t('Rejected'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    }
  };

  return (
    <div className="flex flex-col gap-[22px]">
      <PageHeader title={t('Payouts')} subtitle={t('Instructor requests to withdraw their share of course sales.')} />

      {all && (
        <StatCards
          items={[
            { label: t('Pending requests'), value: money(pendingSum), note: `${counts.pending} ${counts.pending === 1 ? t('request') : t('requests')}` },
            { label: t('Paid out'), value: money(paidSum), note: t('all time') },
            { label: t('Instructor share owed'), value: money(Math.max(0, (instructorShare || 0) - paidSum)), note: t('from Payments') },
          ]}
        />
      )}

      <FilterPills
        value={tab}
        onChange={setTab}
        items={STATUSES.map((s) => ({ key: s, label: t({ pending: 'Pending', paid: 'Paid', rejected: 'Rejected' }[s]), count: all ? counts[s] : undefined }))}
      >
        <SearchBox value={search} onChange={setSearch} placeholder={t('Search instructor…')} />
      </FilterPills>

      {!all ? (
        <p className="text-sm text-lms-muted">{t('Loading…')}</p>
      ) : (
        <DataTable
          cols={COLS}
          empty={rows.length === 0 ? (
            <EmptyPanel bare icon={BadgeDollarSign} title={search ? t('No instructors match your search.') : ({ pending: t('No pending payout requests'), paid: t('No payouts paid yet'), rejected: t('No rejected payouts') }[tab])}>
              {!search && tab === 'pending' && t('Instructors request a payout from their Earnings page once they have an available balance.')}
            </EmptyPanel>
          ) : null}
        >
          {rows.map((p) => (
            <TableRow key={p.id} cols={COLS}>
              <PersonCell name={(p.member || {}).name} email={(p.member || {}).email} />
              <span className="text-[15px] font-bold text-lms-ink lms-num">{money(p.amount)}</span>
              <span className="truncate text-[13.5px] text-lms-body">{p.method || '—'}</span>
              <span className="text-[13.5px] text-lms-sub lms-num">{money((p.balance || {}).available)}</span>
              <span className="text-[13px] text-lms-sub">{fmtDate(p.requested_at)}</span>
              <span className="flex justify-end gap-2">
                {statusOf(p) === 'pending' ? (
                  <>
                    <button type="button" onClick={() => resolve(p, 'reject')} className="lms-press h-[38px] rounded-[10px] border border-lms-sale bg-lms-surface px-3.5 text-[13.5px] font-bold text-lms-sale hover:bg-lms-raised">
                      {t('Reject')}
                    </button>
                    <button type="button" onClick={() => resolve(p, 'pay')} className="lms-press h-[38px] rounded-[10px] bg-lms-accent px-3.5 text-[13.5px] font-bold text-lms-accent-ink hover:brightness-110">
                      {t('Mark as paid')}
                    </button>
                  </>
                ) : statusBadge(statusOf(p))}
              </span>
            </TableRow>
          ))}
        </DataTable>
      )}

      <p className="flex items-center gap-2 text-[13px] text-lms-sub">
        <AlertCircle size={15} className="shrink-0 text-lms-tag-alt-ink" />
        {t('Send the money by the requested method first — “Mark as paid” only records it.')}
      </p>
    </div>
  );
}
